import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { HardDrive, Plus, MoreHorizontal, RefreshCw } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useVolumes, useDeleteVolume, type Volume } from '@/api/opennebula';
import {
  VolumeCreateDialog,
  VolumeAttachDialog,
  VolumeResizeDialog,
  VolumeSnapshotDialog,
  VolumeDetachDialog,
} from './volumes/VolumeDialogs';
import { ListPageShell, TableSkeleton, ErrorState, linkStyle, Dash, mbToGB } from './components/ListPageShell';

type RowAction = 'attach' | 'detach' | 'resize' | 'snapshot' | 'delete';

/**
 * Standalone block volumes for a compute service. Mirrors the legacy console's
 * top-level Volumes page: list + create, with per-row actions gated on whether
 * the volume is attached (Attach/Delete when free; Detach/Resize/Snapshot when
 * attached). Attached-volume actions resolve the owning instance's disk index.
 */
export function Volumes() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: volumes = [], isLoading, isError, refetch } = useVolumes(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [action, setAction] = useState<{ kind: RowAction; volume: Volume } | null>(null);

  const base = `/services/compute/${serviceId}`;

  const remove = useDeleteVolume(serviceId!, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Volume removed' });
      setAction(null);
    },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const close = () => setAction(null);
  const active = (kind: RowAction) => (action?.kind === kind ? action.volume : null);

  const columns: ColumnDef<Volume, unknown>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name || row.original.id}</span>,
    },
    {
      id: 'type',
      header: 'Volume type',
      cell: ({ row }) => (
        <Badge tone={row.original.type === 'standard' ? 'neutral' : 'info'} appearance="subtle" size="sm">
          {VOLUME_TYPE_LABEL[row.original.type] ?? row.original.type}
        </Badge>
      ),
    },
    {
      id: 'persistent',
      header: 'Persistent',
      cell: ({ row }) => (row.original.persistent === '1' ? 'Yes' : 'No'),
    },
    {
      id: 'attached',
      header: 'Attached VM',
      cell: ({ row }) =>
        row.original.attached?.instance_id ? (
          <Link to={`${base}/instances/${row.original.attached.instance_id}`} style={linkStyle}>
            {row.original.attached.name}
          </Link>
        ) : (
          <Dash />
        ),
    },
    {
      id: 'size',
      header: 'Size',
      cell: ({ row }) => <span>{mbToGB(row.original.size)} GB</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const v = row.original;
        const attached = !!v.attached?.instance_id;
        return (
          <div style={{ textAlign: 'right' }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label="Volume actions">
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {attached ? (
                  <>
                    <DropdownMenuItem onSelect={() => setAction({ kind: 'resize', volume: v })}>Resize</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setAction({ kind: 'snapshot', volume: v })}>Snapshot</DropdownMenuItem>
                    <DropdownMenuItem tone="danger" onSelect={() => setAction({ kind: 'detach', volume: v })}>Detach</DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onSelect={() => setAction({ kind: 'attach', volume: v })}>Attach</DropdownMenuItem>
                    <DropdownMenuItem tone="danger" onSelect={() => setAction({ kind: 'delete', volume: v })}>Remove</DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const createBtn = (
    <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
      Create volume
    </Button>
  );

  return (
    <ListPageShell
      icon={<HardDrive size={22} />}
      title="Volumes"
      description="Persistent block volumes you can attach to and detach from instances."
      actions={
        <>
          <Button size="sm" variant="secondary" startIcon={<RefreshCw size={15} />} onClick={() => refetch()}>
            Refresh
          </Button>
          {createBtn}
        </>
      }
    >
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <ErrorState description="Could not load volumes for this service." onRetry={() => refetch()} />
      ) : (
        // DataTable owns the bordered frame; the empty placeholder renders
        // inside it via `emptyState`, so empty and populated share one frame
        // (no extra Card wrapper, no double border).
        <DataTable
          columns={columns}
          data={volumes}
          getRowId={(v) => String(v.id)}
          emptyState={
            <EmptyState
              icon={<HardDrive size={28} />}
              title="No volumes"
              description="Create a block volume to attach extra storage to your instances."
              actions={createBtn}
            />
          }
        />
      )}

      <VolumeCreateDialog open={createOpen} onOpenChange={setCreateOpen} serviceId={serviceId!} />
      <VolumeAttachDialog serviceId={serviceId!} volume={active('attach')} onClose={close} />
      <VolumeDetachDialog serviceId={serviceId!} volume={active('detach')} onClose={close} />
      <VolumeResizeDialog serviceId={serviceId!} volume={active('resize')} onClose={close} />
      <VolumeSnapshotDialog serviceId={serviceId!} volume={active('snapshot')} onClose={close} />

      <ConfirmDialog
        open={action?.kind === 'delete'}
        onOpenChange={(o) => !o && close()}
        title="Delete volume?"
        description={`Permanently delete “${action?.volume.name}”. This is irreversible and may cause data loss.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => action?.volume.id && remove.mutate(action.volume.id)}
      />
    </ListPageShell>
  );
}

const VOLUME_TYPE_LABEL: Record<string, string> = {
  hiops: 'High IOPS SSD',
  standard: 'Standard',
  nvme: 'NVMe SSD',
};
